/**
 * Ops Portal - Users Page
 * Manage tenant users and permissions
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Users, UserPlus, Search, Mail, Phone, Shield, 
  Loader2, MoreVertical, Edit, Trash2, RefreshCw
} from 'lucide-react';

export default function OpsUsersPage() {
  const { tenant } = useTrackOticAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'STAFF'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    // Demo users data
    const demoUsers = [
      { id: 1, full_name: 'Apollo Admin', email: 'admin@apollo.com', phone: '+91 98765 43210', role: 'DISTRIBUTOR_ADMIN', status: 'active', last_login: '2026-03-04T06:30:00Z' },
      { id: 2, full_name: 'Rajesh Kumar', email: 'rajesh@apollo.com', phone: '+91 98765 43211', role: 'ZONE_MANAGER', status: 'active', last_login: '2026-03-04T05:00:00Z' },
      { id: 3, full_name: 'Priya Sharma', email: 'priya@apollo.com', phone: '+91 98765 43212', role: 'STORE_STAFF', status: 'active', last_login: '2026-03-04T04:30:00Z' },
      { id: 4, full_name: 'Amit Patel', email: 'amit@apollo.com', phone: '+91 98765 43213', role: 'ZONE_MANAGER', status: 'active', last_login: '2026-03-03T18:00:00Z' },
      { id: 5, full_name: 'Sneha Reddy', email: 'sneha@apollo.com', phone: '+91 98765 43214', role: 'STORE_STAFF', status: 'active', last_login: '2026-03-03T15:00:00Z' },
      { id: 6, full_name: 'Vikram Singh', email: 'vikram@apollo.com', phone: '+91 98765 43215', role: 'DELIVERY_AGENT', status: 'active', last_login: '2026-03-04T06:00:00Z' },
      { id: 7, full_name: 'Anita Desai', email: 'anita@apollo.com', phone: '+91 98765 43216', role: 'DELIVERY_AGENT', status: 'inactive', last_login: '2026-02-28T12:00:00Z' },
      { id: 8, full_name: 'Rahul Nair', email: 'rahul@apollo.com', phone: '+91 98765 43217', role: 'STORE_STAFF', status: 'active', last_login: '2026-03-04T03:00:00Z' },
    ];
    
    setUsers(demoUsers);
    setLoading(false);
  };

  const getRoleBadge = (role) => {
    const styles = {
      DISTRIBUTOR_ADMIN: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      ZONE_MANAGER: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      STORE_STAFF: 'bg-green-500/20 text-green-400 border-green-500/30',
      DELIVERY_AGENT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      STAFF: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    };
    const labels = {
      DISTRIBUTOR_ADMIN: 'Admin',
      ZONE_MANAGER: 'Zone Manager',
      STORE_STAFF: 'Store Staff',
      DELIVERY_AGENT: 'Delivery Agent',
      STAFF: 'Staff',
    };
    return <Badge className={styles[role]}>{labels[role] || role}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const handleCreateUser = () => {
    toast.success(`User ${newUser.full_name} created successfully`);
    setCreateDialogOpen(false);
    setNewUser({ full_name: '', email: '', phone: '', role: 'STAFF' });
    fetchUsers();
  };

  const filteredUsers = users.filter(user => {
    if (roleFilter !== 'all' && user.role !== roleFilter) return false;
    if (searchQuery && 
        !user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !user.email.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const roles = [...new Set(users.map(u => u.role))];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-teal-500" />
            User Management
          </h1>
          <p className="text-slate-400 mt-1">Manage users and permissions for {tenant?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchUsers} variant="outline" className="border-slate-700 text-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Add New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Full Name</Label>
                  <Input
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    placeholder="Enter full name"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="email@company.com"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Role</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="STAFF">Staff</SelectItem>
                      <SelectItem value="STORE_STAFF">Store Staff</SelectItem>
                      <SelectItem value="DELIVERY_AGENT">Delivery Agent</SelectItem>
                      <SelectItem value="ZONE_MANAGER">Zone Manager</SelectItem>
                      <SelectItem value="DISTRIBUTOR_ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateUser} className="w-full bg-teal-600 hover:bg-teal-700">
                  Create User
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-white">{users.length}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{users.filter(u => u.status === 'active').length}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">Admins</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">{users.filter(u => u.role === 'DISTRIBUTOR_ADMIN').length}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Delivery Agents</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{users.filter(u => u.role === 'DELIVERY_AGENT').length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">User</TableHead>
              <TableHead className="text-slate-400">Contact</TableHead>
              <TableHead className="text-slate-400">Role</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Last Login</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} className="border-slate-800">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
                        {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.full_name}</p>
                        <p className="text-slate-500 text-sm">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Phone className="w-4 h-4 text-slate-500" />
                      {user.phone}
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {new Date(user.last_login).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
